import { Component, Input, OnDestroy } from '@angular/core';
import { LoadingService } from 'customer-portal-framework-lib';
import { AuthService } from 'ps-ng-auth-lib';
import { Observable, of } from 'rxjs';
import { ObjectInformation } from 'src/app/dto/object-information.dto';
import { ObjectLocationDataService } from '../object-location-data.service';
import { environment } from 'src/environments/environment';
import { catchError, filter, map, switchMap, take } from 'rxjs/operators';
import { ObjectReport } from './object-reports/object-report.dto';
import { ErrorHandlerService } from 'src/app/service/error-handler/error-handler.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'isd-object-information',
  templateUrl: './object-information.component.html',
  styleUrls: ['./object-information.component.scss']
})
export class ObjectInformationComponent implements OnDestroy {
  objectImageUrl$: Observable<SafeUrl>;
  imageLoadingKey = 'imageLoadingKey';
  controlsLoadingKey = 'controlsLoadingKey';
  reportsLoadingKey = 'reportsLoadingKey';

  objectControls$: Observable<string[]>;
  objectReports$: Observable<ObjectReport[]>;

  private _objectInformation: ObjectInformation;

  get objectInformation(): ObjectInformation {
    return this._objectInformation;
  }

  @Input() set objectInformation(objectInfo: ObjectInformation) {
    this._objectInformation = objectInfo;

    this.loadObjectImage();
    this.loadObjectControls();
    this.loadObjectReports();
  }

  private objectImageObjectUrl: string | null = null;

  constructor(
    private readonly objectDataService: ObjectLocationDataService,
    private readonly errorHandler: ErrorHandlerService,
    private readonly auth: AuthService,
    private readonly sanitizer: DomSanitizer,
    readonly loadingService: LoadingService
  ) {}

  ngOnDestroy(): void {
    if (this.objectImageObjectUrl) {
      URL.revokeObjectURL(this.objectImageObjectUrl);
    }
  }

  private toTrustedImageUrl(url: string): SafeUrl {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  private loadObjectImage(): void {
    const noImageFound$ = of(
      this.toTrustedImageUrl('/assets/img/no_img_found.jpg')
    );
    if (!this._objectInformation.hasImage) {
      this.objectImageUrl$ = noImageFound$;
      return;
    }

    const imageApiUrl = `${environment.bolApiHost}/${environment.bolApiBaseUrl}/objectLocations/${this._objectInformation.objectNumber}/Image`;

    this.objectImageUrl$ = this.auth.getAccessToken(imageApiUrl).pipe(
      filter(token => !!token),
      take(1),
      switchMap(() =>
        this.objectDataService.getBase64ObjectImageByObjectNumber(
          this._objectInformation.objectNumber
        )
      ),
      map(objectUrl => {
        if (this.objectImageObjectUrl) {
          URL.revokeObjectURL(this.objectImageObjectUrl);
        }
        this.objectImageObjectUrl = objectUrl;
        return this.toTrustedImageUrl(objectUrl);
      }),
      catchError(() => noImageFound$)
    );
  }

  private loadObjectReports(): void {
    this.loadingService.startLoading(this.reportsLoadingKey);
    this.objectReports$ = this.objectDataService
      .getObjectReports(this._objectInformation.objectNumber)
      .pipe(
        this.loadingService.pipeStopLoading(this.reportsLoadingKey),
        this.errorHandler.pipeCatchError()
      );
  }

  private loadObjectControls(): void {
    this.loadingService.startLoading(this.controlsLoadingKey);
    this.objectControls$ = this.objectDataService
      .getObjectControls(this._objectInformation.objectNumber)
      .pipe(
        this.loadingService.pipeStopLoading(this.controlsLoadingKey),
        this.errorHandler.pipeCatchError()
      );
  }
}
